import { Controller } from 'stimulus';
import setFrameSource from '@/js/util/set_frame_source';

// The dropdown's margin from the trigger plus a little room from the viewport edge.
const VIEWPORT_GAP = 8;

export default class extends Controller {
  static targets = ['input', 'trigger', 'label', 'search', 'frame', 'option', 'dropdown'];
  static values = { url: String };

  #searchTimer = null;
  #searching = false;

  disconnect() {
    clearTimeout(this.#searchTimer);
    this.close();
  }

  toggle() {
    if (this.element.classList.contains('is-open')) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.element.classList.add('is-open');
    this.triggerTarget.setAttribute('aria-expanded', 'true');
    this.dropdownTarget.showPopover();
    this.#anchor();
    window.addEventListener('resize', this.#anchor);
    window.addEventListener('scroll', this.#anchor, true);
    this.searchTarget.focus();
  }

  close() {
    const hadFocus = this.dropdownTarget.contains(document.activeElement);
    this.element.classList.remove('is-open');
    this.triggerTarget.setAttribute('aria-expanded', 'false');
    window.removeEventListener('resize', this.#anchor);
    window.removeEventListener('scroll', this.#anchor, true);

    if (this.dropdownTarget.matches(':popover-open')) {
      this.dropdownTarget.hidePopover();
    }

    if (hadFocus) {
      this.triggerTarget.focus();
    }
  }

  focusTrigger() {
    this.triggerTarget.focus();
  }

  dismiss(event) {
    if (!this.element.classList.contains('is-open')) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.close();
  }

  closeOnOutsideClick({ target }) {
    if (!this.element.contains(target)) {
      this.close();
    }
  }

  closeOnFocusOut({ relatedTarget }) {
    if (relatedTarget && !this.element.contains(relatedTarget)) {
      this.close();
    }
  }

  closeOnRestore(event) {
    if (event.persisted) {
      this.close();
    }
  }

  search() {
    this.#searching = true;
    clearTimeout(this.#searchTimer);
    this.#searchTimer = setTimeout(() => this.#loadResults(), 250);
  }

  settle() {
    const src = this.frameTarget.getAttribute('src');
    const loaded = src
      ? (new URL(src, window.location.origin).searchParams.get('search') ?? '')
      : null;
    if (loaded === this.#query) {
      this.#searching = false;
    }
  }

  response(event) {
    if (event.detail?.fetchResponse?.succeeded === false) {
      event.preventDefault();
      this.#fail();
    }
  }

  failed(event) {
    event.preventDefault();
    this.#fail();
  }

  navigate(event) {
    // An IME composes with the arrows and Enter; keyCode 229 is the commit some browsers send.
    if (event.isComposing || event.keyCode === 229) {
      return;
    }

    const options = this.optionTargets.filter((option) => option.offsetParent !== null);
    const index = options.indexOf(document.activeElement);

    if (event.key === 'ArrowDown' && options.length > 0) {
      event.preventDefault();
      options[Math.min(index + 1, options.length - 1)].focus();
    } else if (event.key === 'ArrowUp' && index >= 0) {
      event.preventDefault();
      (options[index - 1] ?? this.searchTarget).focus();
    } else if (event.key === 'Enter' && event.target === this.searchTarget) {
      event.preventDefault();
      if (this.#searching && this.hasFrameTarget) {
        clearTimeout(this.#searchTimer);
        this.#loadResults();
      } else {
        options[0]?.click();
      }
    }
  }

  // Read as written: Stimulus params parse as JSON, so a label like "1.50" would turn into 1.5.
  select({ currentTarget }) {
    const value = currentTarget.getAttribute('data-ui--search-select-value-param');
    const label = currentTarget.getAttribute('data-ui--search-select-label-param');
    if (this.hasInputTarget) {
      this.inputTarget.value = value;
      this.inputTarget.dispatchEvent(new Event('input', { bubbles: true }));
    }
    this.labelTarget.textContent = label;
    this.labelTarget.classList.remove('is-placeholder');
    this.close();
    this.dispatch('change', { detail: { value, label } });
  }

  // Opens upward when it doesn't fit below and there is more room above, and never runs past the
  // viewport, so its footer stays reachable from a trigger near the bottom of a dialog.
  #anchor = () => {
    const { top, bottom, left, width } = this.triggerTarget.getBoundingClientRect();
    const { style } = this.dropdownTarget;
    const spaceBelow = window.innerHeight - bottom - VIEWPORT_GAP;
    const spaceAbove = top - VIEWPORT_GAP;

    style.removeProperty('--search-select-max-height');
    const above = this.dropdownTarget.offsetHeight > spaceBelow && spaceAbove > spaceBelow;

    style.setProperty('--search-select-top', above ? 'auto' : `${bottom}px`);
    style.setProperty('--search-select-bottom', above ? `${window.innerHeight - top}px` : 'auto');
    style.setProperty('--search-select-left', `${left}px`);
    style.setProperty('--search-select-width', `${width}px`);
    style.setProperty('--search-select-max-height', `${above ? spaceAbove : spaceBelow}px`);
  };

  get #query() {
    return this.searchTarget.value.trim();
  }

  #loadResults() {
    setFrameSource(this.frameTarget, this.urlValue, { search: this.#query });
  }

  #fail() {
    this.#searching = false;
    const message = document.createElement('p');
    message.className = 'ui--search-select-status';
    message.textContent = 'Could not load results';
    this.frameTarget.replaceChildren(message);
  }
}
